import React, { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LoginFormModel } from 'src/models/loginModel';
import { removeItem, errorMsg } from '../utilities/utils';
import ToastMessage from 'src/components/ToastContainer';
import { useLoginActions } from 'src/store/login/actions';
import { RootState } from 'src/store/reducers/state';
import FormFieldError from '../common/formFieldError';
import * as AppConstants from '../utilities/constants';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from 'src/config/msalConfig';
import { useVideosActions } from 'src/store/videos/actions';
type locationState = {
  from: string;
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginAction = useLoginActions(dispatch);
  const videoActions = useVideosActions(dispatch);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const loginType = Number(searchParams.get('sa'));
  const returnUrl = searchParams.get('returnUrl');
  const { from } = (location.state as locationState) || {};
  const [isSSOLogin, setIsSSOLogin] = useState(false);
  const [msLoginAttempted, setMsLoginAttempted] = useState(false);
  const msLoginRef = useRef(false);

  const { instance } = useMsal();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginFormModel>();

  useEffect(() => {
    removeItem('token');
    removeItem('role');
    removeItem('userData');

    // Clear all MSAL cache
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      accounts.forEach((account) => {
        instance.setActiveAccount(null);
      });
    }

    sessionStorage.clear();

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('msal.')) {
        localStorage.removeItem(key);
      }
    });
  }, [instance]);

  const handleLogin = (data: LoginFormModel) => {
    loginAction.sendLoginRequest(data);
  };

  const handleMicrosoftLogin = async () => {
    if (msLoginAttempted || msLoginRef.current) {
      console.log('Microsoft login already in progress');
      return;
    }

    msLoginRef.current = true;
    setMsLoginAttempted(true);

    try {
      const response = await instance.loginPopup(loginRequest);

      console.log('Microsoft login successful:', response);

      if (!response || !response.account) {
        console.error('No account information received from Microsoft');
        errorMsg('Invalid response from Microsoft. Please try again.');
        msLoginRef.current = false;
        setMsLoginAttempted(false);
        return;
      }

      const email = response.account.username;

      if (!email || email.trim() === '') {
        console.error('Email is missing from Microsoft response');
        errorMsg('Unable to retrieve email from Microsoft account. Please try again.');
        msLoginRef.current = false;
        setMsLoginAttempted(false);
        return;
      }

      const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
      if (!emailRegex.test(email)) {
        console.error('Invalid email format from Microsoft');
        errorMsg('Invalid email format received from Microsoft. Please try again.');
        msLoginRef.current = false;
        setMsLoginAttempted(false);
        return;
      }

      const payloadData = { Email: email };
      console.log('SSO payloadData', payloadData);
      setIsSSOLogin(true);
      loginAction.sendSSOLoginRequest(payloadData);

      setTimeout(() => {
        msLoginRef.current = false;
      }, 2000);
    } catch (error: any) {
      console.error('Microsoft login error:', error);

      // Handle specific MSAL errors
      if (error.errorCode === 'user_cancelled' || error.errorMessage?.includes('user_cancelled')) {
        errorMsg('Microsoft sign-in was cancelled. Please try again.');
      } else if (error.errorCode === 'popup_window_error') {
        errorMsg('Popup was blocked by your browser. Please allow popups and try again.');
      } else if (error.errorCode === 'monitor_window_timeout') {
        errorMsg('Login timed out. Please try again.');
      } else {
        errorMsg('Microsoft sign-in failed. Please try again or use email/password login.');
      }

      msLoginRef.current = false;
      setMsLoginAttempted(false);
    }
  };

  const { loginError, loading, isLoggedIn, isCallbackLink, domainList, defaultDateFormat } = useSelector(
    (state: RootState) => {
      return {
        loginError: state.login.error,
        loading: state.login.loading,
        isLoggedIn: state.login.isLoggedIn,
        domainList: state.login.domainList,
        isCallbackLink: state.login.isCallbackLink,
        defaultDateFormat: state.login.defaultDateFormat
      };
    }
  );

  useEffect(() => {
    if (isLoggedIn) {
      videoActions.getProductAccessRequest();
      loginAction.getDefaultdateFormatRequest();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn && Object.keys(defaultDateFormat).length && !defaultDateFormat?.accountRestricted) {
      const localData = localStorage.getItem('userData');
      const userData = localData && JSON.parse(localData);

      const finalDestination =
        returnUrl && returnUrl.trim() !== '' && returnUrl !== 'null' ? returnUrl : '/';

      // Skip MFA for SSO logins
      if (isSSOLogin) {
        navigate(finalDestination);
        setIsSSOLogin(false);
      } else {
        if (userData.isMfaSetupEnabled) {
          const authToken = localStorage.getItem('token');
          if (authToken) {
            sessionStorage.setItem(
              'pendingMfaAuth',
              JSON.stringify({
                token: authToken,
                userData: userData,
                timestamp: Date.now()
              })
            );
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            localStorage.removeItem('userData');
          }

          if (finalDestination === '/') {
            navigate('/account/CodeVerification');
          } else {
            navigate('/account/CodeVerification?returnUrl=' + finalDestination);
          }
        } else {
          if (finalDestination === '/') {
            navigate('/account/MultiFactorAuthentication');
          } else {
            navigate('/account/MultiFactorAuthentication?returnUrl=' + finalDestination);
          }
        }
      }

      loginAction.resetLoginStateRequest();
    }
  }, [isLoggedIn, isSSOLogin, returnUrl, navigate, loginAction, defaultDateFormat]);

  useEffect(() => {
    if (Object.keys(loginError).length) {
      console.log('Login error:', loginError);

      if (loginError.customerror != undefined && loginError.customerror) {
        errorMsg(loginError.error_description);
      } else {
        errorMsg('Something went wrong. Please try again.');
      }
      msLoginRef.current = false;
      setMsLoginAttempted(false);
    }
  }, [loginError]);

  useEffect(() => {
    removeItem('token');
    removeItem('role');
    if (isCallbackLink) {
      loginAction.removeCallbackLinkRequest();
    }
  }, [isCallbackLink, loginAction]);

  return (
    <div>
      <ToastMessage />

      <section
        className="bg-gray-100 h-screen"
        style={{
          backgroundImage: `url(../../assets/img/wcc-background-image.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}>
        <img
          className="w-64 absolute top-4 left-4"
          src="../../assets/img/wcc-logo.png"
          alt="logo"
        />
        <div className="container px-6 h-full mx-auto">
          <div className="flex justify-center items-center h-full">
            <div className="xl:w-3/5 lg:w-3/5 md:w-8/12 w-12/12">
              <div className="block bg-white shadow-lg rounded-xl">
                <div className="lg:flex-wrap g-0">
                  <div className=" px-4 md:px-0">
                    <div className="md:px-8 px-5 py-16">
                      <div className="mb-2">
                        <div className="flex justify-center">
                          <h1 className="font-black text-2xl">Welcome Back!</h1>
                        </div>

                        <div className="text-center mb-4">
                          <button
                            className="btn-primary mb-3 microsoft-btn"
                            type="button"
                            onClick={handleMicrosoftLogin}
                            disabled={msLoginAttempted}>
                            <span className="mr-2">
                              <i className="fab fa-microsoft"></i>
                            </span>
                            <span>
                              {msLoginAttempted
                                ? 'OPENING MICROSOFT SIGN IN...'
                                : 'SIGN IN WITH MICROSOFT'}
                            </span>
                          </button>
                        </div>

                        <div className="separator-line">
                          <span className="separator-text">OR</span>
                        </div>
                      </div>
                      {
                        defaultDateFormat?.accountRestricted && (
                          <p className="text-red-600 text-sm text-center mb-4 font-bold">
                            Account restrictions apply.
                          </p>
                        )
                      }
                      <form onSubmit={handleSubmit(handleLogin)}>
                        <div className="mb-4">
                          <label className="mb-1 block font-bold">Work Email</label>
                          <input
                            type="text"
                            className="input-style"
                            id="UserName"
                            {...register('UserName', {
                              required: true,
                              maxLength: 200,
                              pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                            })}
                          />
                          {errors.UserName?.type === 'required' && (
                            <FormFieldError message={AppConstants.requiredField} />
                          )}
                          {errors.UserName?.type === 'pattern' && (
                            <FormFieldError message={AppConstants.notValidEmail} />
                          )}
                          {errors.UserName?.type === 'maxLength' && (
                            <FormFieldError message={AppConstants.maxLengthReached} />
                          )}
                        </div>
                        <div className="mb-4">
                          <label className="mb-1 block font-bold">Password</label>
                          <input
                            id="Password"
                            type="password"
                            className="input-style"
                            {...register('Password', { required: true })}
                          />
                          {errors.Password?.type === 'required' && (
                            <FormFieldError message={AppConstants.requiredField} />
                          )}

                          {loginType ? (
                            <div className="mt-3">
                              <label className="mb-1 block">Select Domain</label>
                              <select
                                className="input-style"
                                {...register('DomainId', { required: true })}>
                                {domainList && domainList.length ? (
                                  domainList.map((domain, idx) => (
                                    <option key={idx} value={domain.id}>
                                      {domain.name}
                                    </option>
                                  ))
                                ) : (
                                  <>
                                    <option value={'0C7C3EDC-4490-48D0-BB45-CD7295E3647C'}>
                                      Appdev
                                    </option>
                                    <option value={'15AC404A-F196-4E93-B3BC-D72486A6A7AD'}>
                                      Associationsdev
                                    </option>
                                    <option value={'04C98F10-5ABC-41DA-A689-7B65FCFAC48B'}>
                                      EngineersAustraliadev
                                    </option>
                                    <option value={'1D0F0114-B6C2-47B0-96D8-7728F96AA387'}>
                                      opteddev
                                    </option>
                                    <option value={'27466dfa-c6a7-48fa-89cc-6a34cc71c6c9'}>
                                      apptest3
                                    </option>
                                  </>
                                )}
                              </select>
                            </div>
                          ) : (
                            ''
                          )}
                          <div className="custom-form-check flex items-center justify-between my-4">
                            <div className="form-check flex items-center justify-start">
                              <input
                                className="form-check-input check-box-input focus:shadow-none transition duration-200 bg-no-repeat bg-center bg-contain float-left checked:bg-blue-600 checked:border-blue-600"
                                type="checkbox"
                              />
                              <label className="ml-1">Remember me</label>
                            </div>
                            <Link
                              to="/account/ForgetPassword"
                              className="text-right text-purple-600">
                              Forgot password?
                            </Link>
                          </div>
                        </div>
                        <div className=" mb-4 mt-6">
                          <button
                            className="btn-primary m-0 w-full px-16"
                            type="submit"
                            disabled={loading}
                            style={{ backgroundColor: '#6638ff', borderColor: '#6638ff' }}>
                            {loading ? (
                              <span className="flex items-center justify-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                </svg>
                                Signing in...
                              </span>
                            ) : (
                              'Login'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
